import proj4 from 'proj4'

export type Bbox = [number, number, number, number]

/**
 * CRS geograficos: as coordenadas ja sao lat/lng, entao dispensam reprojecao.
 * SIRGAS 2000 (4674) e o mais comum em dados brasileiros; a diferenca para
 * WGS84 fica abaixo de um metro, irrelevante para exibir a imagem no mapa.
 */
const GEOGRAPHIC_EPSG = new Set([
	4326, // WGS 84
	4674, // SIRGAS 2000
	4618, // SAD69
	4225, // Corrego Alegre
	4269, // NAD83
])

export class GeoTiffProjectionProvider {
	static isGeographic(epsg: number): boolean {
		return GEOGRAPHIC_EPSG.has(epsg)
	}

	/**
	 * Registra sob demanda as projecoes que aparecem nos GeoTIFFs do cliente e
	 * devolve o codigo proj4 correspondente, ou null se nao for reconhecida.
	 *
	 * Duplica a logica de GeoTiffRasterRenderer.provider (modulo dashboard). A
	 * copia e deliberada: extrair de la exigiria mexer no raster do dashboard,
	 * que esta em producao. Unificar quando houver um terceiro consumidor.
	 */
	static ensureDefinition(epsg: number): string | null {
		const code = `EPSG:${epsg}`
		if (proj4.defs(code)) return code

		if (this.isGeographic(epsg)) {
			proj4.defs(code, '+proj=longlat +ellps=GRS80 +no_defs')
			return code
		}
		if (epsg === 3857) {
			proj4.defs(
				code,
				'+proj=merc +a=6378137 +b=6378137 +lat_ts=0 +lon_0=0 +x_0=0 +y_0=0 +k=1 +units=m +nadgrids=@null +no_defs',
			)
			return code
		}
		// WGS 84 / UTM north e south — EPSG 326xx e 327xx
		if (epsg >= 32601 && epsg <= 32660) {
			proj4.defs(code, `+proj=utm +zone=${epsg - 32600} +datum=WGS84 +units=m +no_defs`)
			return code
		}
		if (epsg >= 32701 && epsg <= 32760) {
			proj4.defs(code, `+proj=utm +zone=${epsg - 32700} +south +datum=WGS84 +units=m +no_defs`)
			return code
		}
		// SIRGAS 2000 / UTM north — EPSG 31965..31976 → zones 11N..22N
		if (epsg >= 31965 && epsg <= 31976) {
			proj4.defs(
				code,
				`+proj=utm +zone=${epsg - 31954} +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs`,
			)
			return code
		}
		// SIRGAS 2000 / UTM south — EPSG 31977..31985 → zones 17S..25S
		if (epsg >= 31977 && epsg <= 31985) {
			proj4.defs(
				code,
				`+proj=utm +zone=${epsg - 31960} +south +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs`,
			)
			return code
		}
		// SAD69 / UTM south — EPSG 29177..29185 → zones 17S..25S
		if (epsg >= 29177 && epsg <= 29185) {
			proj4.defs(
				code,
				`+proj=utm +zone=${epsg - 29160} +south +ellps=aust_SA +towgs84=-66.87,4.37,-38.52,0,0,0,0 +units=m +no_defs`,
			)
			return code
		}
		// Corrego Alegre / UTM south — EPSG 22521..22525 → zones 21S..25S
		if (epsg >= 22521 && epsg <= 22525) {
			proj4.defs(
				code,
				`+proj=utm +zone=${epsg - 22500} +south +ellps=intl +towgs84=-206,172,-6,0,0,0,0 +units=m +no_defs`,
			)
			return code
		}
		return null
	}

	static reprojectBboxToLatLng(bbox: Bbox, epsg: number): Bbox | null {
		if (this.isGeographic(epsg)) return bbox

		const code = this.ensureDefinition(epsg)
		if (!code) return null

		const transformer = proj4(code, 'EPSG:4326')
		const [minX, minY, maxX, maxY] = bbox
		const corners: Array<[number, number]> = [
			[minX, minY],
			[maxX, minY],
			[maxX, maxY],
			[minX, maxY],
		]
		const lonLat = corners.map((corner) => transformer.forward(corner))
		const lons = lonLat.map((point) => point[0])
		const lats = lonLat.map((point) => point[1])
		return [Math.min(...lons), Math.min(...lats), Math.max(...lons), Math.max(...lats)]
	}

	/** Um bbox so serve ao Mapbox se estiver dentro dos limites de lat/lng. */
	static isPlausibleLatLng(bbox: Bbox): boolean {
		const [west, south, east, north] = bbox
		return (
			bbox.every((value) => Number.isFinite(value)) &&
			west >= -180 &&
			east <= 180 &&
			south >= -90 &&
			north <= 90 &&
			east > west &&
			north > south
		)
	}
}
